import React, { type CSSProperties } from "react";
import { cn } from "@/lib/utils";

type Tone = "pass" | "warn" | "fail" | "brand";

const RING_COLOR: Record<Tone, string> = {
  pass: "var(--vb-pass)",
  warn: "var(--vb-warn)",
  fail: "var(--vb-fail)",
  brand: "var(--vb-brand)",
};

type Props = {
  /** Filled portion (e.g. signals surfaced). */
  value: number;
  /** Total (e.g. 19 AI Trust Signals). */
  max: number;
  /** Diameter in px (prototype default 92). */
  size?: number;
  tone?: Tone;
  /** Center label; defaults to `value/max`. */
  label?: React.ReactNode;
  /** When set, the ring is exposed as a single labeled image (role="img") and
   *  the center numerals are hidden from AT to avoid a bare "12/19" reading. */
  ariaLabel?: string;
  className?: string;
};

/** Exposure donut, ported from the prototype's `.ring` - a conic-gradient track
 *  driven by a `--pct` custom property, with a punched-out center hole. */
export function Donut({ value, max, size = 92, tone = "pass", label, ariaLabel, className }: Props) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const style = {
    width: size,
    height: size,
    // both consumed by .vb-ring__track in vb-components.css
    "--pct": `${pct}%`,
    "--vb-ring-color": RING_COLOR[tone],
  } as CSSProperties;

  return (
    <div
      className={cn("vb-ring", className)}
      style={style}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
    >
      <div className="vb-ring__track" />
      <div className="vb-ring__hole">
        <span className="vb-ring__n tnum" aria-hidden={ariaLabel ? true : undefined}>
          {label ?? `${value}/${max}`}
        </span>
      </div>
    </div>
  );
}

export default Donut;
